import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Header, Footer } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Info, ExternalLink, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const HTTP_PAYMENT_REQUIRED = 402;
const MIN_REQUEST_LENGTH = 50;
const WHATSAPP_LINK = "https://wa.me/message/CPZJFHPCCSBMH1";

const ORDER_COPY = {
  fr: {
    genericError: "Impossible d'envoyer la commande. Le serveur n'a pas renvoyé de détail.",
    errorStep: "Étape",
    debugId: "ID debug",
    technicalDetail: "Détail technique",
    steps: ["Type", "Video", "Demande", "Contact", "Confirmation"],
    lockedEdit: "Cette commande est déjà en cours. La modification est verrouillée.",
    loadError: "Impossible de charger la commande.",
    invalidVideo: "Collez un lien SwissTransfer valide.",
    minRequest: `Votre demande doit contenir au moins ${MIN_REQUEST_LENGTH} caractères.`,
    whatsappRequired: "Le numéro WhatsApp est requis.",
    checklistRequired: "Confirmez les 3 points de vérification vidéo avant de valider.",
    updateSuccess: "Commande mise à jour avec succès.",
    createSuccess: "Commande validée. Aucun crédit supplémentaire ne sera consommé.",
    successTitle: "Commande validée",
    successText: "Mathis va analyser votre vidéo. Vous pouvez modifier votre demande tant que l'analyse n'a pas commencé.",
    typeLabel: "Type :",
    estimatedDelay: "Délai estimé :",
    estimatedDelayValue: "2 à 4 jours selon les analyses en cours",
    whatsappLabel: "WhatsApp :",
    follow: "Voir le suivi",
    question: "Question",
    editTitle: "Modifier ma commande",
    newTitle: "Nouvelle commande",
    editSubtitle: "Modifiable tant que l'analyse n'a pas commencé.",
    newSubtitle: "Suivez les étapes, votre crédit sera consommé seulement à la validation.",
    analysisType: "Type d'analyse",
    collectiveTitle: "Match Collectif",
    collectiveDesc: "Analyse tactique de l'équipe",
    individualTitle: "Match Individuel",
    individualDesc: "Focus sur vos performances",
    videoTitle: "Lien vidéo",
    videoHelp: "Uploadez votre vidéo sur SwissTransfer puis collez le lien ici.",
    swissTransfer: "Aller sur SwissTransfer",
    videoLabel: "Lien SwissTransfer",
    requestTitle: "Votre demande",
    requestLabel: "Que souhaitez-vous analyser ?",
    requestPlaceholder: "Ex: Je souhaite une analyse de notre pressing, nos transitions, les points à corriger...",
    minChars: "caractères minimum",
    contactTitle: "Contact",
    whatsappInput: "Numéro WhatsApp",
    whatsappHelp: "Uniquement si nous avons besoin d'une précision.",
    confirmationTitle: "Confirmation",
    videoSummary: "Vidéo :",
    notProvidedF: "Non renseignée",
    notProvidedM: "Non renseigné",
    checklistTitle: "Vérification avant validation",
    checklist: [
      ["video_complete", "La vidéo est complète"],
      ["link_accessible", "Le lien est accessible"],
      ["match_visible", "Le match est visible correctement"],
    ],
    creditNote: "Le crédit sera consommé uniquement si la commande est bien créée.",
    back: "Retour",
    continue: "Continuer",
    update: "Mettre à jour",
    submit: "Valider ma commande",
  },
  en: {
    genericError: "Unable to send the order. The server did not return any detail.",
    errorStep: "Step",
    debugId: "Debug ID",
    technicalDetail: "Technical detail",
    steps: ["Type", "Video", "Request", "Contact", "Confirm"],
    lockedEdit: "This order is already in progress. Editing is locked.",
    loadError: "Unable to load the order.",
    invalidVideo: "Paste a valid SwissTransfer link.",
    minRequest: `Your request must contain at least ${MIN_REQUEST_LENGTH} characters.`,
    whatsappRequired: "WhatsApp number is required.",
    checklistRequired: "Confirm the 3 video checks before submitting.",
    updateSuccess: "Order updated successfully.",
    createSuccess: "Order confirmed. No extra credit will be consumed.",
    successTitle: "Order confirmed",
    successText: "Mathis will analyze your video. You can edit your request until the analysis has started.",
    typeLabel: "Type:",
    estimatedDelay: "Estimated delivery:",
    estimatedDelayValue: "2 to 4 days depending on current analyses",
    whatsappLabel: "WhatsApp:",
    follow: "View tracking",
    question: "Question",
    editTitle: "Edit my order",
    newTitle: "New order",
    editSubtitle: "Editable until the analysis has started.",
    newSubtitle: "Follow the steps. Your credit is consumed only after validation.",
    analysisType: "Analysis type",
    collectiveTitle: "Team Match",
    collectiveDesc: "Tactical team analysis",
    individualTitle: "Individual Match",
    individualDesc: "Focus on your performance",
    videoTitle: "Video link",
    videoHelp: "Upload your video to SwissTransfer, then paste the link here.",
    swissTransfer: "Go to SwissTransfer",
    videoLabel: "SwissTransfer link",
    requestTitle: "Your request",
    requestLabel: "What would you like analyzed?",
    requestPlaceholder: "Example: I would like analysis of our pressing, transitions, and improvement areas...",
    minChars: "minimum characters",
    contactTitle: "Contact",
    whatsappInput: "WhatsApp number",
    whatsappHelp: "Only if we need a clarification.",
    confirmationTitle: "Confirmation",
    videoSummary: "Video:",
    notProvidedF: "Not provided",
    notProvidedM: "Not provided",
    checklistTitle: "Checks before validation",
    checklist: [
      ["video_complete", "The video is complete"],
      ["link_accessible", "The link is accessible"],
      ["match_visible", "The match is clearly visible"],
    ],
    creditNote: "The credit will only be consumed if the order is successfully created.",
    back: "Back",
    continue: "Continue",
    update: "Update",
    submit: "Submit my order",
  },
};

const formatOrderError = (err, copy) => {
  const detail = err.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const parts = [];
    if (detail.message) parts.push(detail.message);
    if (detail.step) parts.push(`${copy.errorStep}: ${detail.step}`);
    if (detail.debug_id) parts.push(`${copy.debugId}: ${detail.debug_id}`);
    if (detail.technical_error) parts.push(`${copy.technicalDetail}: ${detail.technical_error}`);
    return parts.join(" | ");
  }

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg || item.message || JSON.stringify(item)).join(" ");
  }

  return copy.genericError;
};

const OrderPage = () => {
  const { user, checkAuth } = useAuth();
  const { language } = useLanguage();
  const copy = ORDER_COPY[language] || ORDER_COPY.fr;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingOrderId = searchParams.get("order_id");
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    order_type: "collective",
    video_link: "",
    specific_request: "",
    whatsapp: user?.whatsapp || ""
  });
  const [qualityChecklist, setQualityChecklist] = useState({
    video_complete: false,
    link_accessible: false,
    match_visible: false
  });

  useEffect(() => {
    const loadExistingOrder = async () => {
      if (!editingOrderId) return;
      setLoading(true);
      try {
        const { data } = await api.get(`/orders/${editingOrderId}`);
        if (data.status !== "pending") {
          toast.error(copy.lockedEdit);
          navigate("/dashboard");
          return;
        }
        setFormData({
          order_type: data.order_type || "collective",
          video_link: data.video_link || "",
          specific_request: data.specific_request || "",
          whatsapp: data.whatsapp || user?.whatsapp || ""
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || copy.loadError);
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };
    loadExistingOrder();
  }, [editingOrderId, navigate, user?.whatsapp, copy.lockedEdit, copy.loadError]);

  const validateStep = () => {
    if (step === 1 && (!formData.video_link.startsWith("http") || !formData.video_link.includes("swisstransfer"))) {
      toast.error(copy.invalidVideo);
      return false;
    }
    if (step === 2 && formData.specific_request.length < MIN_REQUEST_LENGTH) {
      toast.error(copy.minRequest);
      return false;
    }
    if (step === 3 && !formData.whatsapp.trim()) {
      toast.error(copy.whatsappRequired);
      return false;
    }
    if (step === 4 && !Object.values(qualityChecklist).every(Boolean)) {
      toast.error(copy.checklistRequired);
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (!validateStep()) return;
    setStep((current) => Math.min(current + 1, copy.steps.length - 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setLoading(true);

    try {
      if (editingOrderId) {
        await api.put(`/orders/${editingOrderId}`, formData);
        toast.success(copy.updateSuccess);
      } else {
        await api.post("/orders", formData);
        await checkAuth();
        toast.success(copy.createSuccess);
      }
      setSuccess(true);
    } catch (err) {
      const message = formatOrderError(err, copy);
      toast.error(message, { duration: 12000 });

      if (err.response?.status === HTTP_PAYMENT_REQUIRED) {
        navigate("/pricing");
      }
    } finally {
      setLoading(false);
    }
  };

  const orderTypeLabel = formData.order_type === "collective" ? copy.collectiveTitle : copy.individualTitle;

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Header />
        <main className="min-h-screen flex items-center justify-center px-6 py-24">
          <div className="max-w-xl w-full bg-[#18181B] border border-white/10 p-8 text-center">
            <div className="w-16 h-16 bg-[#CCFF00]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#CCFF00]" />
            </div>
            <h1 className="text-3xl font-black uppercase text-white mb-3">{copy.successTitle}</h1>
            <p className="text-zinc-400 mb-6">{copy.successText}</p>
            <div className="bg-[#0A0A0A] border border-white/10 p-4 text-left mb-6 space-y-2">
              <p className="text-sm text-white"><span className="text-zinc-500">{copy.typeLabel}</span> {orderTypeLabel}</p>
              <p className="text-sm text-white"><span className="text-zinc-500">{copy.estimatedDelay}</span> {copy.estimatedDelayValue}</p>
              <p className="text-sm text-white"><span className="text-zinc-500">{copy.whatsappLabel}</span> {formData.whatsapp}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/dashboard" className="bg-[#CCFF00] text-black py-3 font-bold uppercase text-sm hover:bg-[#AACC00] transition-colors">{copy.follow}</Link>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="border border-green-500/30 text-green-400 py-3 font-bold uppercase text-sm hover:bg-green-500/10 transition-colors inline-flex items-center justify-center gap-2">
                <ExternalLink className="w-4 h-4" /> {copy.question}
              </a>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <main className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-black uppercase text-white mb-2">
              {editingOrderId ? copy.editTitle : copy.newTitle}
            </h1>
            <p className="text-zinc-500">{editingOrderId ? copy.editSubtitle : copy.newSubtitle}</p>
          </div>

          <div className="grid grid-cols-5 gap-2 mb-8">
            {copy.steps.map((label, index) => (
              <button key={label} type="button" onClick={() => setStep(index)} className={`p-2 text-xs font-bold uppercase border ${index === step ? "bg-[#CCFF00] text-black border-[#CCFF00]" : index < step ? "border-[#CCFF00]/40 text-[#CCFF00]" : "border-white/10 text-zinc-500"}`}>
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="bg-[#18181B] border border-white/10 p-6">
            {step === 0 && (
              <div>
                <h2 className="text-lg font-bold uppercase text-white mb-4">{copy.analysisType}</h2>
                <RadioGroup value={formData.order_type} onValueChange={(value) => setFormData({ ...formData, order_type: value })} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ["collective", copy.collectiveTitle, copy.collectiveDesc],
                    ["individual", copy.individualTitle, copy.individualDesc]
                  ].map(([value, title, desc]) => (
                    <label key={value} className={`flex items-start gap-3 p-4 cursor-pointer border ${formData.order_type === value ? "border-[#CCFF00] bg-[#CCFF00]/5" : "border-white/10 hover:border-white/20"}`}>
                      <RadioGroupItem value={value} id={value} className="mt-1" />
                      <div><span className="font-bold text-white block">{title}</span><span className="text-sm text-zinc-500">{desc}</span></div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 className="text-lg font-bold uppercase text-white mb-4">{copy.videoTitle}</h2>
                <div className="bg-[#0A0A0A] border border-blue-500/30 p-4 mb-4 flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-zinc-400">
                    <p className="mb-2">{copy.videoHelp}</p>
                    <a href="https://www.swisstransfer.com" target="_blank" rel="noopener noreferrer" className="text-[#CCFF00] hover:underline inline-flex items-center gap-1">
                      {copy.swissTransfer} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
                <Label htmlFor="video_link" className="text-white text-sm font-medium">{copy.videoLabel}</Label>
                <Input id="video_link" type="url" required value={formData.video_link} onChange={(e) => setFormData({ ...formData, video_link: e.target.value })} className="mt-2 bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00]" placeholder="https://www.swisstransfer.com/..." />
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="text-lg font-bold uppercase text-white mb-4">{copy.requestTitle}</h2>
                <Label htmlFor="specific_request" className="text-white text-sm font-medium">{copy.requestLabel}</Label>
                <Textarea id="specific_request" required minLength={50} value={formData.specific_request} onChange={(e) => setFormData({ ...formData, specific_request: e.target.value })} className="mt-2 bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] min-h-[170px]" placeholder={copy.requestPlaceholder} />
                <p className="text-xs text-zinc-500 mt-2">{formData.specific_request.length}/50 {copy.minChars}</p>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="text-lg font-bold uppercase text-white mb-4">{copy.contactTitle}</h2>
                <Label htmlFor="whatsapp" className="text-white text-sm font-medium">{copy.whatsappInput}</Label>
                <p className="text-sm text-zinc-500 mb-2">{copy.whatsappHelp}</p>
                <Input id="whatsapp" type="tel" required value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} className="mt-2 bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00]" placeholder="+33 6 00 00 00 00" />
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="text-lg font-bold uppercase text-white mb-4">{copy.confirmationTitle}</h2>
                <div className="space-y-3 bg-[#0A0A0A] border border-white/10 p-4">
                  <p className="text-sm text-white"><span className="text-zinc-500">{copy.typeLabel}</span> {orderTypeLabel}</p>
                  <p className="text-sm text-white break-all"><span className="text-zinc-500">{copy.videoSummary}</span> {formData.video_link || copy.notProvidedF}</p>
                  <p className="text-sm text-white"><span className="text-zinc-500">{copy.whatsappLabel}</span> {formData.whatsapp || copy.notProvidedM}</p>
                </div>
                <div className="mt-5 space-y-3 border border-white/10 bg-[#0A0A0A] p-4">
                  <p className="text-xs uppercase text-zinc-500">{copy.checklistTitle}</p>
                  {copy.checklist.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-3 text-sm text-white">
                      <input
                        type="checkbox"
                        checked={qualityChecklist[key]}
                        onChange={(e) => setQualityChecklist({ ...qualityChecklist, [key]: e.target.checked })}
                        className="h-4 w-4 accent-[#CCFF00]"
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <p className="text-sm text-zinc-500 mt-4">{copy.creditNote}</p>
              </div>
            )}

            <div className="flex gap-3 mt-8">
              {step > 0 && <button type="button" onClick={() => setStep(step - 1)} className="px-5 border border-white/20 text-white py-3 font-bold uppercase text-sm hover:bg-white/5">{copy.back}</button>}
              {step < 4 ? (
                <button type="button" onClick={nextStep} className="flex-1 bg-[#CCFF00] text-black py-3 font-bold uppercase text-sm hover:bg-[#AACC00]">{copy.continue}</button>
              ) : (
                <button type="submit" disabled={loading} className="flex-1 bg-[#CCFF00] text-black py-3 font-bold uppercase text-sm hover:bg-[#AACC00] disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingOrderId ? copy.update : copy.submit}
                </button>
              )}
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OrderPage;
